import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import "@/styles/theme.css";

const KEY = "inviktus-theme";
const ThemeContext = createContext(null);
const media = () => window.matchMedia("(prefers-color-scheme: dark)");
const resolve = (pref) => (pref === "system" ? (media().matches ? "dark" : "light") : pref);

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  const [pref, setPref] = useState(() => localStorage.getItem(KEY) || "system");
  const [mode, setMode] = useState(() => resolve(pref));

  // Account choice wins; an explicit local choice made before login is saved to a "system" account.
  useEffect(() => {
    if (!user) return;
    if (user.theme === "dark" || user.theme === "light") setPref(user.theme);
    else if (pref !== "system") api.put("/auth/me/theme", { theme: pref }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.theme]);

  useEffect(() => {
    const apply = () => {
      const m = resolve(pref);
      setMode(m);
      document.documentElement.dataset.theme = m;
    };
    apply();
    localStorage.setItem(KEY, pref);
    if (pref !== "system") return undefined;
    const mq = media();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [pref]);

  const toggle = useCallback(() => {
    const next = mode === "dark" ? "light" : "dark";
    setPref(next);
    if (user) api.put("/auth/me/theme", { theme: next }).catch(() => {});
  }, [mode, user]);

  return <ThemeContext.Provider value={{ mode, pref, toggle }}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
